import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, Tabs, Tag, Empty, Skeleton, Space, message, Avatar, Modal, Popconfirm } from 'antd';
import {
  RocketOutlined, CalendarOutlined, EnvironmentOutlined, DollarOutlined,
  MessageOutlined, CheckOutlined, StopOutlined, DeleteOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import { resolveActiveTeam, setActiveTeamId } from '../../utils/activeTeam';

const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';

function fmtDate(iso) {
  if (!iso) return 'A combinar';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const STATUS_LABEL = {
  aberto: { label: 'Aberto', color: 'green' },
  em_negociacao: { label: 'Negociando', color: 'gold' },
  confirmado: { label: 'Confirmado', color: 'blue' },
  cancelado: { label: 'Cancelado', color: 'red' },
};

// Meus amistosos: dono ve TODOS os amistosos do time dele (host) organizados
// por status. Permite abrir chat, aceitar proposta, cancelar, apagar.
// Diferente do FriendlyMatches que mostra o FEED PUBLICO de amistosos de
// outros times.
export default function MyFriendlies() {
  const [myTeams, setMyTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('active');

  useEffect(() => {
    api.get('/community/team-owner/my').then(r => {
      const teams = r.data?.teams || [];
      setMyTeams(teams);
      if (!selectedTeamId && teams.length > 0) {
        // Respeita ultima escolha (localStorage) em vez de sempre teams[0]
        const t = resolveActiveTeam(teams);
        if (t) setSelectedTeamId(t.id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!selectedTeamId) return;
    setLoading(true);
    try {
      const r = await api.get('/community/friendlies/mine', { params: { team_id: selectedTeamId } });
      setMatches(r.data?.matches || []);
    } catch { message.error('Erro ao carregar'); }
    setLoading(false);
  }, [selectedTeamId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (tab === 'active') return matches.filter(m => m.status === 'aberto' || m.status === 'em_negociacao');
    if (tab === 'confirmed') return matches.filter(m => m.status === 'confirmado');
    if (tab === 'cancelled') return matches.filter(m => m.status === 'cancelado');
    return matches;
  }, [matches, tab]);

  const cancel = (id) => {
    Modal.confirm({
      title: 'Cancelar esse amistoso?', okText: 'Cancelar amistoso', cancelText: 'Voltar', okType: 'danger',
      onOk: async () => {
        try { await api.post(`/community/friendlies/${id}/cancel`); message.success('Cancelado'); load(); }
        catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
      },
    });
  };
  const remove = (id) => {
    Modal.confirm({
      title: 'Apagar esse amistoso?',
      content: 'Vai remover propostas e conversas. Não dá pra desfazer.',
      okText: 'Apagar', cancelText: 'Voltar', okType: 'danger',
      onOk: async () => {
        try { await api.delete(`/community/friendlies/${id}`); message.success('Removido'); load(); }
        catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
      },
    });
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
      <div style={{
        borderRadius: 16, padding: 20, marginBottom: 16, color: '#fff',
        background: `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
      }}>
        <div style={{ fontSize: 22, fontWeight: 900 }}>
          <RocketOutlined /> Meus amistosos
        </div>
        <div style={{ opacity: 0.9, marginTop: 4 }}>
          Todos os amistosos que teu time abriu — pendentes, confirmados e cancelados.
        </div>
      </div>

      {/* Seletor de time (se dono de mais de 1) */}
      {myTeams.length > 1 && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {myTeams.map(t => (
            <Button key={t.id} size="small"
              type={t.id === selectedTeamId ? 'primary' : 'default'}
              onClick={() => { setSelectedTeamId(t.id); setActiveTeamId(t.id); }}
              style={t.id === selectedTeamId ? { background: BRAND, borderColor: BRAND } : {}}>
              {t.name}
            </Button>
          ))}
        </div>
      )}

      <Card style={{ borderRadius: 16 }}>
        <Tabs
          activeKey={tab} onChange={setTab}
          items={[
            { key: 'active',    label: `Ativos (${matches.filter(m => m.status === 'aberto' || m.status === 'em_negociacao').length})` },
            { key: 'confirmed', label: `Confirmados (${matches.filter(m => m.status === 'confirmado').length})` },
            { key: 'cancelled', label: `Cancelados (${matches.filter(m => m.status === 'cancelado').length})` },
            { key: 'all',       label: `Todos (${matches.length})` },
          ]}
        />
        {loading ? <Skeleton active /> :
          filtered.length === 0 ? <Empty description="Nada por aqui" /> :
          <div style={{ display: 'grid', gap: 12 }}>
            {filtered.map(m => {
              const st = STATUS_LABEL[m.status] || { label: m.status, color: 'default' };
              return (
                <Card key={m.id} size="small" style={{ borderRadius: 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Avatar size={40} src={absoluteUploadUrl(m.host_logo)} icon={<RocketOutlined />}
                      style={{ background: BRAND }} />
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ fontWeight: 700 }}>
                        {m.opponent_team_name ? `vs ${m.opponent_team_name}` : 'Sem oponente'}
                      </div>
                      <Space size={10} wrap style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                        <span><CalendarOutlined /> {fmtDate(m.date_time)}</span>
                        {m.city && <span><EnvironmentOutlined /> {m.city}/{m.state}</span>}
                        {m.quadra && <span>· {m.quadra}</span>}
                        {m.valor > 0 && <span><DollarOutlined /> R$ {Number(m.valor).toFixed(2)}</span>}
                      </Space>
                    </div>
                    <Tag color={st.color} style={{ margin: 0 }}>{st.label}</Tag>
                  </div>
                  {m.proposals_count > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: BRAND_DARK }}>
                      <MessageOutlined /> {m.proposals_count} proposta{m.proposals_count > 1 ? 's' : ''}
                      {m.unread_messages > 0 && (
                        <Tag color="red" style={{ marginLeft: 8 }}>{m.unread_messages} nova{m.unread_messages > 1 ? 's' : ''}</Tag>
                      )}
                    </div>
                  )}
                  <Space style={{ marginTop: 10 }} wrap>
                    <Button size="small" icon={<MessageOutlined />}
                      onClick={() => window.location.href = `/time/amistosos?match_id=${m.id}`}>
                      Ver detalhes
                    </Button>
                    {(m.status === 'aberto' || m.status === 'em_negociacao') && (
                      <Popconfirm title="Apagar?" onConfirm={() => remove(m.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />}>Apagar</Button>
                      </Popconfirm>
                    )}
                    {m.status !== 'cancelado' && m.status !== 'confirmado' && (
                      <Button size="small" icon={<StopOutlined />} onClick={() => cancel(m.id)}>Cancelar</Button>
                    )}
                  </Space>
                </Card>
              );
            })}
          </div>
        }
      </Card>
    </div>
  );
}
